import React, { useEffect, useState } from 'react';
import { Button, Input, Label } from 'reactstrap';
import MainLoader from '../../components/MainLoader';
import { konkursyService } from '../../service/Konkursy';

let temporaryIdCounter = 0;

const temporaryId = (prefix) => {
  temporaryIdCounter += 1;
  return `${prefix}-${Date.now()}-${temporaryIdCounter}`;
};

const blankTemplate = () => ({
  Id: '',
  Name: 'Nowy formularz',
  FormType: 'IMPREZA',
  Description: '',
  IsActive: 1,
  Sections: []
});

const blankSection = () => ({
  Id: temporaryId('section'),
  Name: 'Nowa sekcja',
  Description: '',
  Questions: []
});

const blankQuestion = (questionType) => ({
  Id: temporaryId('question'),
  Content: questionType === 'SCALE' ? 'Jak oceniasz ten element?' : 'Wpisz tresc pytania',
  QuestionType: questionType,
  ScaleMin: questionType === 'SCALE' ? 1 : '',
  ScaleMax: questionType === 'SCALE' ? 10 : '',
  IsRequired: 1
});

const blankLibrarySection = () => ({
  Id: '',
  Name: '',
  Description: '',
  SortOrder: 1,
  Questions: []
});

const blankLibraryQuestion = () => ({
  Id: '',
  Content: '',
  QuestionType: 'TEXT',
  ScaleMin: '',
  ScaleMax: '',
  IsRequired: 1
});

const blankAssignment = () => ({
  Id: temporaryId('assignment'),
  Template_Id: '',
  Impreza_Id: '',
  Etap_Id: '',
  FormType: 'IMPREZA',
  IsPublished: 0,
  OpensAt: '',
  ClosesAt: ''
});

const clone = (value) => JSON.parse(JSON.stringify(value));
const sameId = (left, right) => String(left) === String(right);
const isEmptyFlag = (value) => value === '' || value === null || value === undefined || Number(value) === 0 || value === false;

const getErrorMessage = (error) => (
  error?.response?.data?.message || error?.message || 'Wystapil nieoczekiwany blad'
);

const normalizeBuilderPackage = (payload) => {
  if (Array.isArray(payload)) {
    return { templates: payload, library: { sections: [], questions: [] }, assignments: [], imprezy: [] };
  }

  return {
    templates: payload?.templates || [],
    library: {
      sections: payload?.library?.sections || [],
      questions: payload?.library?.questions || []
    },
    assignments: payload?.assignments || [],
    imprezy: payload?.imprezy || []
  };
};

const copyQuestion = (question) => ({
  Id: temporaryId('question'),
  Content: question.Content || '',
  QuestionType: question.QuestionType === 'SCALE' ? 'SCALE' : 'TEXT',
  ScaleMin: question.QuestionType === 'SCALE' ? (question.ScaleMin || 1) : '',
  ScaleMax: question.QuestionType === 'SCALE' ? (question.ScaleMax || 10) : '',
  IsRequired: !isEmptyFlag(question.IsRequired) ? 1 : 0
});

const copySection = (section) => ({
  Id: temporaryId('section'),
  Name: section.Name || 'Sekcja',
  Description: section.Description || '',
  Questions: (section.Questions || []).map(copyQuestion)
});

const DropZone = ({ onDrop, label = 'Upusc tutaj' }) => (
  <div
    className="builder-drop-zone"
    onDragOver={(event) => event.preventDefault()}
    onDrop={onDrop}
  >
    <span>{label}</span>
  </div>
);

const PaletteItem = ({ icon, title, description, payload, onAdd }) => (
  <button
    type="button"
    className="builder-palette-item"
    draggable
    onDragStart={(event) => {
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('application/json', JSON.stringify(payload));
    }}
    onClick={onAdd}
  >
    <i className={`fa ${icon}`} aria-hidden="true"></i>
    <span>
      <strong>{title}</strong>
      <small>{description}</small>
    </span>
    <i className="fa fa-arrows builder-palette-drag" aria-hidden="true"></i>
  </button>
);

const QuestionEditor = ({ question, sectionId, index, update, remove, dragStart, saveAsPreset }) => {
  const isScale = question.QuestionType === 'SCALE';

  return (
    <div className="builder-question-card">
      <div className="builder-question-topline">
        <span
          className="builder-drag-handle"
          draggable
          title="Przeciagnij pytanie"
          onDragStart={(event) => dragStart(event, {
            kind: 'question',
            questionId: question.Id,
            fromSectionId: sectionId
          })}
        >
          <i className="fa fa-bars" aria-hidden="true"></i>
        </span>
        <span className="builder-item-number">Pytanie {index + 1}</span>
        <button type="button" className="builder-icon-button" onClick={saveAsPreset} title="Zapisz pytanie jako wzorzec">
          <i className="fa fa-bookmark-o" aria-hidden="true"></i>
        </button>
        <button type="button" className="builder-icon-button danger" onClick={remove} title="Usun pytanie">
          <i className="fa fa-trash" aria-hidden="true"></i>
        </button>
      </div>

      <Label>Tresc pytania</Label>
      <Input
        type="textarea"
        rows="2"
        value={question.Content || ''}
        onChange={(event) => update({ Content: event.target.value })}
      />

      <div className="builder-question-options">
        <div>
          <Label>Typ odpowiedzi</Label>
          <Input
            type="select"
            value={question.QuestionType}
            onChange={(event) => {
              const type = event.target.value;
              update({
                QuestionType: type,
                ScaleMin: type === 'SCALE' ? (question.ScaleMin || 1) : '',
                ScaleMax: type === 'SCALE' ? (question.ScaleMax || 10) : ''
              });
            }}
          >
            <option value="TEXT">Tekst</option>
            <option value="SCALE">Skala</option>
          </Input>
        </div>

        {isScale && (
          <>
            <div>
              <Label>Od</Label>
              <Input
                type="number"
                value={question.ScaleMin ?? ''}
                onChange={(event) => update({ ScaleMin: event.target.value })}
              />
            </div>
            <div>
              <Label>Do</Label>
              <Input
                type="number"
                value={question.ScaleMax ?? ''}
                onChange={(event) => update({ ScaleMax: event.target.value })}
              />
            </div>
          </>
        )}

        <label className="builder-required-option">
          <Input
            type="checkbox"
            checked={Boolean(Number(question.IsRequired))}
            onChange={(event) => update({ IsRequired: event.target.checked ? 1 : 0 })}
          />
          Wymagane
        </label>
      </div>

      <div className="builder-answer-preview">
        {isScale ? (
          <div className="builder-scale-preview">
            <span>{question.ScaleMin || '?'}</span>
            <span className="builder-scale-line"></span>
            <span>{question.ScaleMax || '?'}</span>
          </div>
        ) : (
          <Input disabled placeholder="Tutaj uczestnik wpisze odpowiedz..." />
        )}
      </div>
    </div>
  );
};

const KonkursyAdmin = () => {
  const [templates, setTemplates] = useState([]);
  const [library, setLibrary] = useState({ sections: [], questions: [] });
  const [assignments, setAssignments] = useState([]);
  const [imprezy, setImprezy] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(null);
  const [librarySectionDraft, setLibrarySectionDraft] = useState(blankLibrarySection());
  const [libraryQuestionDraft, setLibraryQuestionDraft] = useState(blankLibraryQuestion());
  const [activeTab, setActiveTab] = useState('builder');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [assignmentsDirty, setAssignmentsDirty] = useState(false);
  const [notice, setNotice] = useState(null);

  const applyPackage = (payload, savedId) => {
    const data = normalizeBuilderPackage(payload);
    setTemplates(data.templates);
    setLibrary(data.library);
    setAssignments((data.assignments || []).map((assignment) => ({
      ...assignment,
      Id: assignment.Id || temporaryId('assignment'),
      Etap_Id: assignment.Etap_Id || '',
      OpensAt: assignment.OpensAt || '',
      ClosesAt: assignment.ClosesAt || ''
    })));
    setImprezy(data.imprezy || []);

    const idToSelect = savedId || selectedId;
    const selected = data.templates.find((item) => sameId(item.Id, idToSelect)) || data.templates[0];
    if (selected) {
      setSelectedId(String(selected.Id));
      setDraft(clone(selected));
    } else {
      setSelectedId('new');
      setDraft(blankTemplate());
    }
  };

  const loadTemplates = () => {
    setLoading(true);
    konkursyService.getBuilder()
      .then((response) => {
        applyPackage(response?.data || {}, '');
        setDirty(false);
        setAssignmentsDirty(false);
      })
      .catch((error) => {
        setSelectedId('new');
        setDraft(blankTemplate());
        setNotice({ type: 'danger', text: getErrorMessage(error) });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const warnBeforeLeaving = (event) => {
      if (!dirty && !assignmentsDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [dirty, assignmentsDirty]);

  const changeDraft = (updater) => {
    setDraft((current) => typeof updater === 'function' ? updater(current) : updater);
    setDirty(true);
    setNotice(null);
  };

  const selectTemplate = (id) => {
    if (dirty && !window.confirm('Odrzucic niezapisane zmiany?')) {
      return;
    }
    const template = templates.find((item) => sameId(item.Id, id));
    if (template) {
      setSelectedId(String(template.Id));
      setDraft(clone(template));
      setDirty(false);
      setNotice(null);
    }
  };

  const createTemplate = () => {
    if (dirty && !window.confirm('Odrzucic niezapisane zmiany?')) {
      return;
    }
    setSelectedId('new');
    setDraft(blankTemplate());
    setDirty(true);
    setNotice(null);
  };

  const updateSection = (sectionId, updater) => {
    changeDraft((current) => ({
      ...current,
      Sections: current.Sections.map((section) => sameId(section.Id, sectionId)
        ? (typeof updater === 'function' ? updater(section) : { ...section, ...updater })
        : section)
    }));
  };

  const insertSectionObject = (index, section) => {
    changeDraft((current) => {
      const sections = [...current.Sections];
      sections.splice(index, 0, copySection(section));
      return { ...current, Sections: sections };
    });
  };

  const insertSection = (index, initialQuestionType = '') => {
    changeDraft((current) => {
      const sections = [...current.Sections];
      const section = blankSection();
      if (initialQuestionType) {
        section.Questions = [blankQuestion(initialQuestionType)];
      }
      sections.splice(index, 0, section);
      return { ...current, Sections: sections };
    });
  };

  const insertQuestionObject = (sectionId, question, index) => {
    updateSection(sectionId, (section) => {
      const questions = [...(section.Questions || [])];
      const targetIndex = typeof index === 'number' ? index : questions.length;
      questions.splice(targetIndex, 0, copyQuestion(question));
      return { ...section, Questions: questions };
    });
  };

  const insertQuestion = (sectionId, questionType, index) => {
    insertQuestionObject(sectionId, blankQuestion(questionType), index);
  };

  const readPayload = (event) => {
    try {
      return JSON.parse(event.dataTransfer.getData('application/json'));
    } catch (error) {
      return null;
    }
  };

  const handleDragStart = (event, payload) => {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/json', JSON.stringify(payload));
  };

  const dropSection = (event, targetIndex) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readPayload(event);
    if (!payload) return;

    if (payload.kind === 'new-section') {
      insertSection(targetIndex);
      return;
    }
    if (payload.kind === 'new-question') {
      insertSection(targetIndex, payload.questionType);
      return;
    }
    if (payload.kind === 'library-section') {
      insertSectionObject(targetIndex, payload.section);
      return;
    }
    if (payload.kind === 'library-question') {
      const section = blankSection();
      section.Questions = [copyQuestion(payload.question)];
      insertSectionObject(targetIndex, section);
      return;
    }
    if (payload.kind !== 'section') return;

    changeDraft((current) => {
      const sections = [...current.Sections];
      const sourceIndex = sections.findIndex((section) => sameId(section.Id, payload.sectionId));
      if (sourceIndex < 0) return current;
      const moved = sections.splice(sourceIndex, 1)[0];
      const adjustedIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      sections.splice(adjustedIndex, 0, moved);
      return { ...current, Sections: sections };
    });
  };

  const dropQuestion = (event, targetSectionId, targetIndex) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readPayload(event);
    if (!payload) return;

    if (payload.kind === 'new-question') {
      insertQuestion(targetSectionId, payload.questionType, targetIndex);
      return;
    }
    if (payload.kind === 'library-question') {
      insertQuestionObject(targetSectionId, payload.question, targetIndex);
      return;
    }
    if (payload.kind !== 'question') return;

    changeDraft((current) => {
      let movedQuestion = null;
      let sourceQuestionIndex = -1;
      const sectionsWithoutQuestion = current.Sections.map((section) => ({
        ...section,
        Questions: (section.Questions || []).filter((question, questionIndex) => {
          if (sameId(question.Id, payload.questionId)) {
            movedQuestion = question;
            sourceQuestionIndex = questionIndex;
            return false;
          }
          return true;
        })
      }));

      if (!movedQuestion) return current;
      return {
        ...current,
        Sections: sectionsWithoutQuestion.map((section) => {
          if (!sameId(section.Id, targetSectionId)) return section;
          const questions = [...section.Questions];
          const adjustedIndex = sameId(payload.fromSectionId, targetSectionId) && sourceQuestionIndex < targetIndex
            ? targetIndex - 1
            : targetIndex;
          questions.splice(Math.min(adjustedIndex, questions.length), 0, movedQuestion);
          return { ...section, Questions: questions };
        })
      };
    });
  };

  const removeSection = (sectionId) => {
    if (!window.confirm('Usunac sekcje razem z jej pytaniami ze szkicu?')) return;
    changeDraft((current) => ({
      ...current,
      Sections: current.Sections.filter((section) => !sameId(section.Id, sectionId))
    }));
  };

  const removeQuestion = (sectionId, questionId) => {
    updateSection(sectionId, (section) => ({
      ...section,
      Questions: section.Questions.filter((question) => !sameId(question.Id, questionId))
    }));
  };

  const save = () => {
    if (!draft.Name.trim()) {
      setNotice({ type: 'danger', text: 'Podaj nazwe formularza.' });
      return;
    }
    setSaving(true);
    setNotice(null);
    konkursyService.saveBuilder(draft)
      .then((response) => {
        applyPackage(response?.data || {}, response.savedId);
        setDirty(false);
        setNotice({ type: 'success', text: response.message || 'Formularz zapisany.' });
      })
      .catch((error) => setNotice({ type: 'danger', text: getErrorMessage(error) }))
      .finally(() => setSaving(false));
  };

  const deleteTemplate = () => {
    if (!draft?.Id || !window.confirm(`Usunac formularz "${draft.Name}"?`)) return;
    setSaving(true);
    setNotice(null);
    konkursyService.deleteTemplate(draft.Id)
      .then((response) => {
        applyPackage(response?.data || {}, '');
        setDirty(false);
        setNotice({ type: 'success', text: response.message || 'Formularz usuniety.' });
      })
      .catch((error) => setNotice({ type: 'danger', text: getErrorMessage(error) }))
      .finally(() => setSaving(false));
  };

  const saveLibraryItem = (type, item) => {
    setSaving(true);
    setNotice(null);
    konkursyService.saveLibraryItem(type, item)
      .then((response) => {
        applyPackage(response?.data || {}, selectedId);
        setLibrarySectionDraft(blankLibrarySection());
        setLibraryQuestionDraft(blankLibraryQuestion());
        setNotice({ type: 'success', text: response.message || 'Wzorzec zapisany.' });
      })
      .catch((error) => setNotice({ type: 'danger', text: getErrorMessage(error) }))
      .finally(() => setSaving(false));
  };

  const deleteLibraryItem = (type, id) => {
    if (!window.confirm('Ukryc ten wzorzec?')) return;
    setSaving(true);
    setNotice(null);
    konkursyService.deleteLibraryItem(type, id)
      .then((response) => {
        applyPackage(response?.data || {}, selectedId);
        setNotice({ type: 'success', text: response.message || 'Wzorzec ukryty.' });
      })
      .catch((error) => setNotice({ type: 'danger', text: getErrorMessage(error) }))
      .finally(() => setSaving(false));
  };

  const addAssignment = () => {
    setAssignments((current) => [...current, blankAssignment()]);
    setAssignmentsDirty(true);
    setNotice(null);
  };

  const updateAssignment = (assignmentId, changes) => {
    setAssignments((current) => current.map((assignment) => {
      if (!sameId(assignment.Id, assignmentId)) return assignment;
      const next = { ...assignment, ...changes };
      if (changes.FormType === 'IMPREZA') {
        next.Etap_Id = '';
      }
      return next;
    }));
    setAssignmentsDirty(true);
    setNotice(null);
  };

  const removeAssignment = (assignmentId) => {
    setAssignments((current) => current.filter((assignment) => !sameId(assignment.Id, assignmentId)));
    setAssignmentsDirty(true);
    setNotice(null);
  };

  const saveAssignments = () => {
    setSaving(true);
    setNotice(null);
    konkursyService.saveAssignments(assignments)
      .then((response) => {
        applyPackage(response?.data || {}, selectedId);
        setAssignmentsDirty(false);
        setNotice({ type: 'success', text: response.message || 'Powiazania zapisane.' });
      })
      .catch((error) => setNotice({ type: 'danger', text: getErrorMessage(error) }))
      .finally(() => setSaving(false));
  };

  const filteredTemplates = (formType) => templates.filter((template) => template.FormType === formType);
  const findImpreza = (id) => imprezy.find((impreza) => sameId(impreza.Id, id));

  if (loading || !draft) return <MainLoader />;

  return (
    <div className="form-builder-page">
      <header className="form-builder-toolbar">
        <div className="form-builder-title">
          <h2>Kreator formularzy ocen</h2>
          <span>{dirty || assignmentsDirty ? 'Niezapisane zmiany' : 'Wszystkie zmiany zapisane'}</span>
        </div>

        <div className="builder-tabs">
          <button type="button" className={activeTab === 'builder' ? 'active' : ''} onClick={() => setActiveTab('builder')}>
            Formularze
          </button>
          <button type="button" className={activeTab === 'assignments' ? 'active' : ''} onClick={() => setActiveTab('assignments')}>
            Powiazania
          </button>
        </div>

        {activeTab === 'builder' && (
          <>
            <div className="form-builder-template-picker">
              <Label for="builder-template">Formularz</Label>
              <Input
                id="builder-template"
                type="select"
                value={selectedId}
                onChange={(event) => selectTemplate(event.target.value)}
              >
                {selectedId === 'new' && <option value="new">Nowy formularz</option>}
                {templates.map((template) => (
                  <option key={template.Id} value={String(template.Id)}>{template.Name}</option>
                ))}
              </Input>
            </div>
            <Button outline color="success" onClick={createTemplate} disabled={saving}>
              <i className="fa fa-plus mr-2" aria-hidden="true"></i>Nowy
            </Button>
            <Button outline color="danger" onClick={deleteTemplate} disabled={saving || !draft.Id}>
              <i className="fa fa-trash mr-2" aria-hidden="true"></i>Usun
            </Button>
            <Button color="success" onClick={save} disabled={saving || !dirty}>
              <i className={`fa ${saving ? 'fa-spinner fa-spin' : 'fa-floppy-o'} mr-2`} aria-hidden="true"></i>
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </Button>
          </>
        )}

        {activeTab === 'assignments' && (
          <>
            <Button outline color="success" onClick={addAssignment} disabled={saving}>
              <i className="fa fa-plus mr-2" aria-hidden="true"></i>Dodaj
            </Button>
            <Button color="success" onClick={saveAssignments} disabled={saving || !assignmentsDirty}>
              <i className={`fa ${saving ? 'fa-spinner fa-spin' : 'fa-floppy-o'} mr-2`} aria-hidden="true"></i>
              {saving ? 'Zapisywanie...' : 'Zapisz powiazania'}
            </Button>
          </>
        )}
      </header>

      {notice && <div className={`builder-notice ${notice.type}`}>{notice.text}</div>}

      {activeTab === 'builder' && (
        <div className="form-builder-workspace">
          <main className="form-builder-canvas">
            <div className="builder-template-card">
              <div className="builder-template-fields">
                <div className="builder-name-field">
                  <Label>Nazwa formularza</Label>
                  <Input
                    value={draft.Name}
                    onChange={(event) => changeDraft({ ...draft, Name: event.target.value })}
                  />
                </div>
                <div>
                  <Label>Typ</Label>
                  <Input
                    type="select"
                    value={draft.FormType}
                    onChange={(event) => changeDraft({ ...draft, FormType: event.target.value })}
                  >
                    <option value="IMPREZA">Ocena imprezy</option>
                    <option value="ETAP">Ocena etapu</option>
                  </Input>
                </div>
                <label className="builder-active-option">
                  <Input
                    type="checkbox"
                    checked={Boolean(Number(draft.IsActive))}
                    onChange={(event) => changeDraft({ ...draft, IsActive: event.target.checked ? 1 : 0 })}
                  />
                  Aktywny
                </label>
              </div>
              <Label>Opis</Label>
              <Input
                type="textarea"
                rows="2"
                value={draft.Description || ''}
                onChange={(event) => changeDraft({ ...draft, Description: event.target.value })}
                placeholder="Krotka informacja wyswietlana przy formularzu"
              />
            </div>

            <DropZone onDrop={(event) => dropSection(event, 0)} label="Przeciagnij tutaj pierwsza sekcje" />
            {draft.Sections.map((section, sectionIndex) => (
              <React.Fragment key={section.Id}>
                <section className="builder-section-card">
                  <div
                    className="builder-section-header"
                    draggable
                    onDragStart={(event) => handleDragStart(event, { kind: 'section', sectionId: section.Id })}
                  >
                    <span className="builder-drag-handle" title="Przeciagnij sekcje">
                      <i className="fa fa-bars" aria-hidden="true"></i>
                    </span>
                    <span>Sekcja {sectionIndex + 1}</span>
                    <button type="button" className="builder-icon-button" onClick={() => saveLibraryItem('section', section)} title="Zapisz sekcje jako wzorzec">
                      <i className="fa fa-bookmark-o" aria-hidden="true"></i>
                    </button>
                    <button type="button" className="builder-icon-button danger" onClick={() => removeSection(section.Id)} title="Usun sekcje">
                      <i className="fa fa-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                  <div className="builder-section-body">
                    <Label>Nazwa sekcji</Label>
                    <Input
                      className="builder-section-name"
                      value={section.Name}
                      onChange={(event) => updateSection(section.Id, { Name: event.target.value })}
                    />
                    <Label>Opis sekcji</Label>
                    <Input
                      type="textarea"
                      rows="2"
                      value={section.Description || ''}
                      onChange={(event) => updateSection(section.Id, { Description: event.target.value })}
                    />

                    <div className="builder-questions">
                      <DropZone
                        onDrop={(event) => dropQuestion(event, section.Id, 0)}
                        label="Dodaj pierwsze pytanie"
                      />
                      {(section.Questions || []).map((question, questionIndex) => (
                        <React.Fragment key={question.Id}>
                          <QuestionEditor
                            question={question}
                            sectionId={section.Id}
                            index={questionIndex}
                            dragStart={handleDragStart}
                            saveAsPreset={() => saveLibraryItem('question', question)}
                            update={(changes) => updateSection(section.Id, (currentSection) => ({
                              ...currentSection,
                              Questions: currentSection.Questions.map((item) => sameId(item.Id, question.Id)
                                ? { ...item, ...changes }
                                : item)
                            }))}
                            remove={() => removeQuestion(section.Id, question.Id)}
                          />
                          <DropZone
                            onDrop={(event) => dropQuestion(event, section.Id, questionIndex + 1)}
                            label="Przenies pytanie tutaj"
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </section>
                <DropZone onDrop={(event) => dropSection(event, sectionIndex + 1)} label="Przenies sekcje tutaj" />
              </React.Fragment>
            ))}

            {draft.Sections.length === 0 && (
              <div className="builder-empty-state">
                <i className="fa fa-file-text-o" aria-hidden="true"></i>
                <h3>Formularz nie ma jeszcze sekcji</h3>
                <p>Przeciagnij sekcje, pytanie albo zapisany wzorzec z prawego panelu.</p>
              </div>
            )}
          </main>

          <aside className="form-builder-palette">
            <div className="builder-palette-sticky">
              <h3>Elementy formularza</h3>

              <h4>Uklad</h4>
              <PaletteItem
                icon="fa-folder-open-o"
                title="Sekcja"
                description="Grupuje powiazane pytania"
                payload={{ kind: 'new-section' }}
                onAdd={() => insertSection(draft.Sections.length)}
              />

              <h4>Pytania</h4>
              <PaletteItem
                icon="fa-align-left"
                title="Pytanie tekstowe"
                description="Odpowiedz wpisywana przez uczestnika"
                payload={{ kind: 'new-question', questionType: 'TEXT' }}
                onAdd={() => {
                  if (draft.Sections.length === 0) insertSection(0, 'TEXT');
                  else insertQuestion(draft.Sections[draft.Sections.length - 1].Id, 'TEXT');
                }}
              />
              <PaletteItem
                icon="fa-sliders"
                title="Skala"
                description="Ocena liczbowa we wskazanym zakresie"
                payload={{ kind: 'new-question', questionType: 'SCALE' }}
                onAdd={() => {
                  if (draft.Sections.length === 0) insertSection(0, 'SCALE');
                  else insertQuestion(draft.Sections[draft.Sections.length - 1].Id, 'SCALE');
                }}
              />

              <h4>Biblioteka sekcji</h4>
              {(library.sections || []).map((section) => (
                <div className="builder-library-row" key={section.Id}>
                  <PaletteItem
                    icon="fa-bookmark"
                    title={section.Name}
                    description={`${(section.Questions || []).length} pyt. wzorcowych`}
                    payload={{ kind: 'library-section', section }}
                    onAdd={() => insertSectionObject(draft.Sections.length, section)}
                  />
                  <button type="button" className="builder-icon-button danger" onClick={() => deleteLibraryItem('section', section.Id)} title="Ukryj wzorzec">
                    <i className="fa fa-trash" aria-hidden="true"></i>
                  </button>
                </div>
              ))}

              <h4>Biblioteka pytan</h4>
              {(library.questions || []).map((question) => (
                <div className="builder-library-row" key={question.Id}>
                  <PaletteItem
                    icon={question.QuestionType === 'SCALE' ? 'fa-sliders' : 'fa-align-left'}
                    title={question.Content}
                    description={question.QuestionType === 'SCALE' ? `Skala ${question.ScaleMin}-${question.ScaleMax}` : 'Pytanie tekstowe'}
                    payload={{ kind: 'library-question', question }}
                    onAdd={() => {
                      if (draft.Sections.length === 0) {
                        const section = blankSection();
                        section.Questions = [copyQuestion(question)];
                        insertSectionObject(0, section);
                      } else {
                        insertQuestionObject(draft.Sections[draft.Sections.length - 1].Id, question);
                      }
                    }}
                  />
                  <button type="button" className="builder-icon-button danger" onClick={() => deleteLibraryItem('question', question.Id)} title="Ukryj wzorzec">
                    <i className="fa fa-trash" aria-hidden="true"></i>
                  </button>
                </div>
              ))}

              <div className="builder-library-editor">
                <h4>Nowy wzorzec pytania</h4>
                <Input
                  type="textarea"
                  rows="2"
                  placeholder="Tresc pytania"
                  value={libraryQuestionDraft.Content}
                  onChange={(event) => setLibraryQuestionDraft({ ...libraryQuestionDraft, Content: event.target.value })}
                />
                <Input
                  type="select"
                  value={libraryQuestionDraft.QuestionType}
                  onChange={(event) => setLibraryQuestionDraft({
                    ...libraryQuestionDraft,
                    QuestionType: event.target.value,
                    ScaleMin: event.target.value === 'SCALE' ? 1 : '',
                    ScaleMax: event.target.value === 'SCALE' ? 10 : ''
                  })}
                >
                  <option value="TEXT">Tekst</option>
                  <option value="SCALE">Skala</option>
                </Input>
                {libraryQuestionDraft.QuestionType === 'SCALE' && (
                  <div className="builder-library-scale">
                    <Input type="number" value={libraryQuestionDraft.ScaleMin} onChange={(event) => setLibraryQuestionDraft({ ...libraryQuestionDraft, ScaleMin: event.target.value })} />
                    <Input type="number" value={libraryQuestionDraft.ScaleMax} onChange={(event) => setLibraryQuestionDraft({ ...libraryQuestionDraft, ScaleMax: event.target.value })} />
                  </div>
                )}
                <Button outline color="success" size="sm" onClick={() => saveLibraryItem('question', libraryQuestionDraft)} disabled={saving || !libraryQuestionDraft.Content.trim()}>
                  Zapisz wzorzec
                </Button>
              </div>

              <div className="builder-library-editor">
                <h4>Nowy wzorzec sekcji</h4>
                <Input
                  placeholder="Nazwa sekcji"
                  value={librarySectionDraft.Name}
                  onChange={(event) => setLibrarySectionDraft({ ...librarySectionDraft, Name: event.target.value })}
                />
                <Input
                  type="textarea"
                  rows="2"
                  placeholder="Opis"
                  value={librarySectionDraft.Description}
                  onChange={(event) => setLibrarySectionDraft({ ...librarySectionDraft, Description: event.target.value })}
                />
                <Button outline color="success" size="sm" onClick={() => saveLibraryItem('section', librarySectionDraft)} disabled={saving || !librarySectionDraft.Name.trim()}>
                  Zapisz sekcje
                </Button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {activeTab === 'assignments' && (
        <main className="builder-assignments">
          {assignments.length === 0 && (
            <div className="builder-empty-state">
              <i className="fa fa-link" aria-hidden="true"></i>
              <h3>Brak powiazan</h3>
              <p>Dodaj formularz do imprezy albo konkretnego etapu. Publiczny guzik mozna podpiac pozniej.</p>
            </div>
          )}

          {assignments.map((assignment, index) => {
            const selectedImpreza = findImpreza(assignment.Impreza_Id);
            const templatesForType = filteredTemplates(assignment.FormType);

            return (
              <section className="builder-assignment-card" key={assignment.Id}>
                <div className="builder-assignment-title">
                  <span>Powiazanie {index + 1}</span>
                  <button type="button" className="builder-icon-button danger" onClick={() => removeAssignment(assignment.Id)} title="Usun powiazanie">
                    <i className="fa fa-trash" aria-hidden="true"></i>
                  </button>
                </div>

                <div className="builder-assignment-grid">
                  <div>
                    <Label>Typ</Label>
                    <Input
                      type="select"
                      value={assignment.FormType}
                      onChange={(event) => updateAssignment(assignment.Id, { FormType: event.target.value, Template_Id: '', Etap_Id: '' })}
                    >
                      <option value="IMPREZA">Ocena imprezy</option>
                      <option value="ETAP">Ocena etapu</option>
                    </Input>
                  </div>
                  <div>
                    <Label>Formularz</Label>
                    <Input
                      type="select"
                      value={assignment.Template_Id || ''}
                      onChange={(event) => updateAssignment(assignment.Id, { Template_Id: event.target.value })}
                    >
                      <option value="">Wybierz formularz</option>
                      {templatesForType.map((template) => (
                        <option key={template.Id} value={template.Id}>{template.Name}</option>
                      ))}
                    </Input>
                  </div>
                  <div>
                    <Label>Impreza</Label>
                    <Input
                      type="select"
                      value={assignment.Impreza_Id || ''}
                      onChange={(event) => updateAssignment(assignment.Id, { Impreza_Id: event.target.value, Etap_Id: '' })}
                    >
                      <option value="">Wybierz impreze</option>
                      {imprezy.map((impreza) => (
                        <option key={impreza.Id} value={impreza.Id}>{impreza.Nazwa} ({impreza.Sezon})</option>
                      ))}
                    </Input>
                  </div>

                  {assignment.FormType === 'ETAP' && (
                    <div>
                      <Label>Etap</Label>
                      <Input
                        type="select"
                        value={assignment.Etap_Id || ''}
                        onChange={(event) => updateAssignment(assignment.Id, { Etap_Id: event.target.value })}
                        disabled={!selectedImpreza}
                      >
                        <option value="">Wybierz etap</option>
                        {(selectedImpreza?.Etapy || []).map((etap) => (
                          <option key={etap.Id} value={etap.Id}>{etap.Nazwa}</option>
                        ))}
                      </Input>
                    </div>
                  )}

                  <div>
                    <Label>Otwarcie</Label>
                    <Input type="datetime-local" value={(assignment.OpensAt || '').replace(' ', 'T')} onChange={(event) => updateAssignment(assignment.Id, { OpensAt: event.target.value.replace('T', ' ') })} />
                  </div>
                  <div>
                    <Label>Zamkniecie</Label>
                    <Input type="datetime-local" value={(assignment.ClosesAt || '').replace(' ', 'T')} onChange={(event) => updateAssignment(assignment.Id, { ClosesAt: event.target.value.replace('T', ' ') })} />
                  </div>
                  <label className="builder-active-option">
                    <Input
                      type="checkbox"
                      checked={Boolean(Number(assignment.IsPublished))}
                      onChange={(event) => updateAssignment(assignment.Id, { IsPublished: event.target.checked ? 1 : 0 })}
                    />
                    Opublikowane
                  </label>
                </div>
              </section>
            );
          })}
        </main>
      )}
    </div>
  );
};

export default KonkursyAdmin;
